import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Country, State } from "country-state-city";
import React, { useEffect, useMemo, useState } from "react";
import CustomButton from "./Button";
import Loader from "./Loader";
import { useToast } from "./Toast";
import apiService from "../Services/api.service";
import { UI, btnSx } from "./ActionDrawer";
import useCurrencies from "../Hooks/useCurrencies";

/* CustomButton in the dashboard button language. CustomButton spreads props
   after its own sx, so ours wins. Behaviour is untouched. */
const Btn = ({ variant = "solid", sx, ...props }) => (
  <CustomButton {...props} sx={{ ...btnSx(variant, props.disabled), ...sx }} />
);

const PRODUCTS = {
  Nova: { label: "scanO Nova Rover", price: 577500 },
  Air: { label: "scanO Air", price: 314999 },
};

const BAG_PRICE = 8000;
const GST_RATES = { Air: 5, Nova: 5, engage: 18, bag: 18 };

// Matches the geo codes actually stored on ScanoPlans.geoPricing (IN/AE/US).
const COUNTRY_TO_GEO = {
  in: "IN",
  india: "IN",
  ae: "AE",
  uae: "AE",
  "united arab emirates": "AE",
  us: "US",
  usa: "US",
  "united states": "US",
  "united states of america": "US",
};
const resolveGeo = (country) =>
  COUNTRY_TO_GEO[(country || "").trim().toLowerCase()] || "IN";

const rupee = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/* GST is reverse-calculated via divide/multiply chains below, which drifts
   off the paisa (e.g. 199999.997 instead of 200000) under plain float math.
   Snap every derived money value to paisa precision at the point it's produced
   so amounts entered by the user come back out exactly what was entered. */
const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

// plansMap always comes from ScanoPlans (fetched via apiService.getPlans) —
// no hardcoded fallback prices, and no plan name is assumed free; a plan
// prices as 0 only when the model itself says its price is 0.
const calcEngageAmount = (plan, tenureMonths, discount, discountType, plansMap = {}) => {
  const selectedPlan = plansMap[plan];
  const months = Math.max(Number(tenureMonths) || 0, 0);

  let grossAmount = 0;
  if (months > 0) {
    if (months % 12 === 0) {
      grossAmount = (selectedPlan?.yearly || 0) * (months / 12);
    } else {
      grossAmount = (selectedPlan?.monthly || 0) * months;
    }
  }

  const preTaxBase = grossAmount / (1 + GST_RATES.engage / 100);
  const safeDiscount = Math.max(Number(discount) || 0, 0);
  let discountToSubtract = 0;
  if (discountType === "percent") {
    const safePercent = Math.min(safeDiscount, 100);
    discountToSubtract = (preTaxBase * safePercent) / 100;
  } else {
    discountToSubtract = Math.min(safeDiscount, preTaxBase);
  }
  const preTaxAfterDiscount = Math.max(preTaxBase - discountToSubtract, 0);
  const amount = round2(preTaxAfterDiscount * (1 + GST_RATES.engage / 100));
  const discountAmount = round2(grossAmount - amount);
  const gstAmount = round2(amount - (amount * 100) / (100 + GST_RATES.engage));

  return { grossAmount: round2(grossAmount), amount, discountAmount, gstAmount };
};

const computeFixedDiscountForTarget = (
  grossAmount,
  gstRatePercent,
  targetAmount,
) => {
  const preTaxBase = grossAmount / (1 + gstRatePercent / 100);
  const targetPreTax =
    Math.max(Number(targetAmount) || 0, 0) / (1 + gstRatePercent / 100);
  const discountToSubtract = Math.max(preTaxBase - targetPreTax, 0);
  return Math.round(Math.min(discountToSubtract, preTaxBase) * 100) / 100;
};

/* The API may hand back an ISO datetime; <input type="date"> needs YYYY-MM-DD. */
const toDateInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().split("T")[0];
};

const SectionCard = ({ title, children, sx }) => (
  <Box
    sx={{
      bgcolor: UI.surface,
      border: "1px solid",
      borderColor: UI.line,
      borderRadius: "8px",
      padding: "14px 16px",
      marginBottom: "14px",
      ...sx,
    }}
  >
    {title && (
      <Box
        sx={{
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: ".2px",
          color: UI.ink2,
          marginBottom: "12px",
        }}
      >
        {title}
      </Box>
    )}
    {children}
  </Box>
);

const ToggleRow = ({ options, value, onChange }) => (
  <Box
    sx={{
      display: "flex",
      flexWrap: "wrap",
      border: "1px solid",
      borderColor: UI.line,
      borderRadius: "4px",
      overflow: "hidden",
      width: "fit-content",
      bgcolor: UI.surface,
    }}
  >
    {options.map((opt) => (
      <Box
        key={String(opt.value)}
        onClick={() => onChange(opt.value)}
        sx={{
          px: 2,
          py: "7px",
          cursor: "pointer",
          bgcolor: value === opt.value ? UI.p : "transparent",
          color: value === opt.value ? UI.surface : UI.ink2,
          fontWeight: value === opt.value ? 500 : 400,
          fontSize: 13,
          transition: "background-color 0.15s ease",
        }}
      >
        {opt.label}
      </Box>
    ))}
  </Box>
);

/* Pill used for the plan and tenure choices. */
const pillSx = (on) => ({
  textTransform: "none",
  boxShadow: "none",
  minWidth: 0,
  borderRadius: "14px",
  padding: "4px 13px",
  fontSize: 13,
  fontWeight: on ? 500 : 400,
  fontFamily: "inherit",
  border: "1px solid",
  borderColor: on ? UI.p : UI.line,
  bgcolor: on ? UI.p : UI.surface,
  color: on ? UI.surface : UI.ink2,
  "&:hover": {
    boxShadow: "none",
    bgcolor: on ? UI.pDark : UI.p50,
    borderColor: UI.p,
  },
});

const PillGroup = ({ options, value, onChange }) => (
  <Box sx={{ display: "flex", gap: "7px", flexWrap: "wrap" }}>
    {options.map((opt) => (
      <Button
        key={opt.value}
        size="small"
        variant={value === opt.value ? "contained" : "outlined"}
        disableElevation
        onClick={() => onChange(opt.value)}
        sx={pillSx(value === opt.value)}
      >
        {opt.label}
      </Button>
    ))}
  </Box>
);

const DiscountFields = ({
  amountLabel,
  amount,
  onAmountChange,
  type,
  onTypeChange,
  /* Pass these two to get the "target amount" calculator underneath. */
  targetGross,
  gstRate,
}) => {
  const [target, setTarget] = useState("");

  const applyTarget = () => {
    onTypeChange("fixed");
    onAmountChange(
      computeFixedDiscountForTarget(targetGross || 0, gstRate || 0, target),
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          width: "fit-content",
        }}
      >
        <TextField
          label={amountLabel}
          type="number"
          size="small"
          value={amount}
          inputProps={{ min: 0 }}
          onWheel={(e) => e.target.blur()}
          onFocus={(e) => e.target.select()}
          onChange={(e) => onAmountChange(e.target.value)}
          sx={{ width: { xs: "100%", sm: 170 } }}
        />
        <TextField
          select
          label="Type"
          size="small"
          value={type}
          onChange={(e) => onTypeChange(e.target.value)}
          sx={{ width: { xs: "100%", sm: 90 } }}
        >
          <MenuItem value="percent">%</MenuItem>
          <MenuItem value="fixed">₹</MenuItem>
        </TextField>
      </Box>

      {targetGross > 0 && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            type="number"
            placeholder="Target amount"
            value={target}
            inputProps={{ min: 0 }}
            onWheel={(e) => e.target.blur()}
            onFocus={(e) => e.target.select()}
            onChange={(e) => setTarget(e.target.value)}
            sx={{ width: 150 }}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={applyTarget}
            disabled={!target}
            sx={{
              textTransform: "none",
              boxShadow: "none",
              fontSize: 13,
              fontWeight: 500,
              borderRadius: "4px",
              minWidth: 0,
              padding: "4px 12px",
              border: "1px solid",
              borderColor: UI.p100,
              bgcolor: UI.surface,
              color: UI.p,
              "&:hover": { bgcolor: UI.p50, borderColor: UI.p },
            }}
          >
            Set
          </Button>
          <Box sx={{ fontSize: 11, color: UI.ink4 }}>
            sets a ₹ discount to hit this incl-GST amount
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Derives the initial editable quote form state from an order's stored quote object.
const buildFormFromQuote = (quote) => {
  const tenureMonths = quote?.tenureMonths || 12;
  return {
    product: quote?.product || "Air",
    currency: quote?.currency || "INR",
    units: quote?.units || 1,
    discount: quote?.discount || 0,
    discountType: quote?.discountType || "percent",
    engagePlan: quote?.engagePlan || "starter",
    tenureMonths,
    customTenure: ![12, 24, 36].includes(tenureMonths),
    engageDiscount: quote?.engageDiscount || 0,
    engageDiscountType: quote?.engageDiscountType || "percent",
    bagRequired: Boolean(quote?.bagRequired),
    bagDiscount: quote?.bagDiscount || 0,
    bagDiscountType: quote?.bagDiscountType || "percent",
    paymentType: quote?.paymentType || "advance",
    paymentTerm: quote?.paymentTerm || "",
    expiryDate: toDateInput(quote?.expiryDate),
    engageAddOns: (quote?.engageAddOns || []).map((addOn) => ({
      plan: addOn.plan,
      tenureMonths: addOn.tenureMonths || 12,
      customTenure: ![12, 24, 36].includes(addOn.tenureMonths),
      discount: addOn.discount || 0,
      discountType: addOn.discountType || "percent",
    })),
  };
};

/* Bill To / Ship To, same fields as the New Order form. State and country are ISO
   codes, like the clinic record. */
const ADDRESS_REQUIRED = [
  ["name", "name"],
  ["addressLine1", "address line 1"],
  ["country", "country"],
  ["state", "state"],
  ["city", "city"],
];

const toAddressForm = (address) => ({
  sameAsClinic: address?.sameAsClinic !== false,
  name: address?.name || "",
  addressLine1: address?.addressLine1 || "",
  addressLine2: address?.addressLine2 || "",
  city: address?.city || "",
  state: address?.state || "",
  pincode: address?.pincode || "",
  country: address?.country || "",
});

const AddressEditor = ({ label, value, onChange, clinicSummary, prefill }) => {
  const set = (field, next) => onChange({ ...value, [field]: next });
  const states = value.country ? State.getStatesOfCountry(value.country) : [];

  // Unticking "same as clinic" on an empty address starts from the clinic's details.
  const toggleSame = (checked) => {
    if (checked) return onChange({ ...value, sameAsClinic: true });
    const empty = !value.name && !value.addressLine1;
    onChange({ ...value, ...(empty ? prefill : {}), sameAsClinic: false });
  };

  return (
    <Box sx={{ marginBottom: "14px" }}>
      <Box sx={{ fontSize: 13, fontWeight: 500, color: UI.ink }}>{label}</Box>
      <FormControlLabel
        control={
          <Checkbox size="small" checked={value.sameAsClinic} onChange={(e) => toggleSame(e.target.checked)} />
        }
        label={<Box sx={{ fontSize: 13, color: UI.ink }}>Same as clinic details</Box>}
      />
      {value.sameAsClinic ? (
        <Box sx={{ fontSize: 12, color: UI.ink3 }}>Will use: {clinicSummary || "—"}</Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, marginTop: "6px" }}>
          <TextField label={`${label} name`} size="small" fullWidth value={value.name} onChange={(e) => set("name", e.target.value)} />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField label="Address line 1" size="small" fullWidth value={value.addressLine1} onChange={(e) => set("addressLine1", e.target.value)} />
            <TextField label="Address line 2" size="small" fullWidth value={value.addressLine2} onChange={(e) => set("addressLine2", e.target.value)} />
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              select
              label="Country"
              size="small"
              fullWidth
              value={value.country}
              onChange={(e) => onChange({ ...value, country: e.target.value, state: "" })}
            >
              {Country.getAllCountries().map((country) => (
                <MenuItem key={country.isoCode} value={country.isoCode}>
                  {country.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="State"
              size="small"
              fullWidth
              value={value.state}
              disabled={!value.country}
              onChange={(e) => set("state", e.target.value)}
            >
              {states.map((state) => (
                <MenuItem key={state.isoCode} value={state.isoCode}>
                  {state.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField label="City" size="small" fullWidth value={value.city} onChange={(e) => set("city", e.target.value)} />
            <TextField label="Pincode" size="small" fullWidth value={value.pincode} onChange={(e) => set("pincode", e.target.value)} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

const EditQuoteModal = ({ open, orderId, onClose, onSaved, mode = "quote" }) => {
  const isInvoiceMode = mode === "invoice";
  const toast = useToast();
  const token = localStorage.getItem("token");
  const currencies = useCurrencies();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [clinicName, setClinicName] = useState("");
  const [clinicCountry, setClinicCountry] = useState("");
  const [quote, setQuote] = useState(null);
  const [editReason, setEditReason] = useState("");

  // order.clinic/order.doctor are references to the shared clinic/doctor
  // records — saving here updates those documents directly, so every order,
  // subscription and ticket for this doctor/clinic picks up the change too.
  const [doctorId, setDoctorId] = useState("");
  const [doctorName, setDoctorName] = useState("");
  const [clinicId, setClinicId] = useState("");
  const [clinicAddress, setClinicAddress] = useState("");
  const [clinicCity, setClinicCity] = useState("");
  const [clinicState, setClinicState] = useState("");
  const [clinicPincode, setClinicPincode] = useState("");
  const [isIdentityEditable, setIdentityEditable] = useState(false);
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [billTo, setBillTo] = useState(toAddressForm());
  const [shipTo, setShipTo] = useState(toAddressForm());

  // clinic.country/clinic.state are stored as ISO codes (confirmed against
  // AddOrder.js's own clinic form: State.getStatesOfCountry(clinic.country)
  // passes the stored value straight in as an ISO code, no name lookup) —
  // same convention here so existing clinic data round-trips correctly.
  const identityCountries = useMemo(() => Country.getAllCountries(), []);
  const identityStates = useMemo(
    () => (clinicCountry ? State.getStatesOfCountry(clinicCountry) : []),
    [clinicCountry],
  );
  const [identitySnapshot, setIdentitySnapshot] = useState(null);

  // No hardcoded fallback — starts empty and is only ever populated from
  // ScanoPlans (via apiService.getPlans).
  const [enginePlans, setEnginePlans] = useState({});
  const [enginePlansError, setEnginePlansError] = useState(false);

  useEffect(() => {
    if (!open || !orderId) return;
    setLoading(true);
    setEditReason("");
    setIdentityEditable(false);
    apiService
      .getOrder(orderId, token)
      .then((res) => {
        setClinicName(res.data.clinic?.name || "");
        setClinicCountry(res.data.clinic?.country || "");
        setClinicId(res.data.clinic?._id || "");
        setClinicAddress(res.data.clinic?.address || "");
        setClinicCity(res.data.clinic?.city || "");
        setClinicState(res.data.clinic?.state || "");
        setClinicPincode(res.data.clinic?.pincode || "");
        setDoctorId(res.data.doctor?._id || "");
        setDoctorName(res.data.doctor?.name || "");
        setBillTo(toAddressForm(res.data.billTo));
        setShipTo(toAddressForm(res.data.shipTo));
        setQuote(buildFormFromQuote(res.data.quote));
      })
      .catch((error) => {
        console.error("Failed to load quote for editing:", error);
        toast.error(
          error.response?.data?.message || "Failed to load quote for editing",
        );
        onClose();
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, orderId]);

  // Refetches/recomputes whenever the modal opens or the clinic's country
  // becomes known (arrives async from getOrder above), so pricing reflects
  // that clinic's actual region instead of always IN.
  useEffect(() => {
    if (!open) return;
    let isMounted = true;
    const geo = resolveGeo(clinicCountry);

    apiService
      .getPlans(token)
      .then((res) => {
        const map = {};
        (res?.plans || []).forEach((plan) => {
          const key = plan.name?.toLowerCase();
          if (!key) return;

          const geoPrice =
            plan.geoPricing?.find((g) => g.geo === geo) || plan.geoPricing?.[0];
          const price = Number(geoPrice?.price || 0);
          const yearly = plan.billingCycle === "monthly" ? round2(price * 12) : price;

          map[key] = {
            label: plan.name,
            yearly,
            monthly: round2(yearly / 12),
          };
        });

        if (isMounted) {
          setEnginePlans(map);
          if (!Object.keys(map).length) setEnginePlansError(true);
        }
      })
      .catch((err) => {
        console.error("Failed to load Engage plans from the plan model:", err.message);
        if (isMounted) setEnginePlansError(true);
      });

    return () => {
      isMounted = false;
    };
  }, [open, clinicCountry, token]);

  const setField = (name, value) =>
    setQuote((prev) => ({ ...prev, [name]: value }));

  const addEngageAddOn = () =>
    setQuote((prev) => {
      // Default to a paid plan other than whichever is already the primary
      // one — whichever plans are actually paid comes from ScanoPlans, not a
      // fixed name list.
      const paidPlanKeys = Object.entries(enginePlans)
        .filter(([, plan]) => plan.yearly > 0)
        .map(([key]) => key);
      const defaultAddOnPlan =
        paidPlanKeys.find((key) => key !== prev.engagePlan) || paidPlanKeys[0] || "";

      return {
        ...prev,
        engageAddOns: [
          ...(prev.engageAddOns || []),
          {
            plan: defaultAddOnPlan,
            tenureMonths: 12,
            customTenure: false,
            discount: 0,
            discountType: "percent",
          },
        ],
      };
    });
  const removeEngageAddOn = (index) =>
    setQuote((prev) => ({
      ...prev,
      engageAddOns: prev.engageAddOns.filter((_, i) => i !== index),
    }));
  const setEngageAddOnField = (index, name, value) =>
    setQuote((prev) => ({
      ...prev,
      engageAddOns: prev.engageAddOns.map((addOn, i) =>
        i === index ? { ...addOn, [name]: value } : addOn,
      ),
    }));

  const pricing = useMemo(() => {
    if (!quote) return null;

    const product = PRODUCTS[quote.product];
    const units = Math.max(Number(quote.units) || 0, 0);
    const rate = Number(product?.price || 0);
    const grossAmount = rate * units;

    // PRODUCT DISCOUNT (discount applied on pre-tax base, then tax added back —
    // matches Zoho's is_inclusive_tax + is_discount_before_tax behavior)
    const discount = Math.max(Number(quote.discount) || 0, 0);
    const productGstRate = GST_RATES[quote.product] || 0;
    const preTaxBase = grossAmount / (1 + productGstRate / 100);
    let discountToSubtract = 0;
    if (quote.discountType === "percent") {
      const safePercent = Math.min(discount, 100);
      discountToSubtract = (preTaxBase * safePercent) / 100;
    } else {
      discountToSubtract = Math.min(discount, preTaxBase);
    }
    const preTaxAfterDiscount = Math.max(preTaxBase - discountToSubtract, 0);
    const productAmount = round2(preTaxAfterDiscount * (1 + productGstRate / 100));
    const discountAmount = round2(grossAmount - productAmount);

    // ENGAGE SUBSCRIPTION (primary plan)
    const primaryEngage = calcEngageAmount(
      quote.engagePlan,
      quote.tenureMonths,
      quote.engageDiscount,
      quote.engageDiscountType,
      enginePlans,
    );
    const engageGrossAmount = primaryEngage.grossAmount;
    const engageAmount = primaryEngage.amount;
    const engageDiscountAmount = primaryEngage.discountAmount;

    const engageAddOnAmounts = (quote.engageAddOns || []).map((addOn) =>
      calcEngageAmount(
        addOn.plan,
        addOn.tenureMonths,
        addOn.discount,
        addOn.discountType,
        enginePlans,
      ),
    );
    const addOnsAmount = engageAddOnAmounts.reduce(
      (sum, item) => sum + item.amount,
      0,
    );
    const addOnsGstAmount = engageAddOnAmounts.reduce(
      (sum, item) => sum + item.gstAmount,
      0,
    );

    // BAG
    const bagGrossAmount = quote.bagRequired ? BAG_PRICE * units : 0;
    const bagPreTaxBase = bagGrossAmount / (1 + GST_RATES.bag / 100);
    const bagDiscount = Math.max(Number(quote.bagDiscount) || 0, 0);
    let bagDiscountToSubtract = 0;
    if (quote.bagDiscountType === "percent") {
      const safePercent = Math.min(bagDiscount, 100);
      bagDiscountToSubtract = (bagPreTaxBase * safePercent) / 100;
    } else {
      bagDiscountToSubtract = Math.min(bagDiscount, bagPreTaxBase);
    }
    const bagPreTaxAfterDiscount = Math.max(
      bagPreTaxBase - bagDiscountToSubtract,
      0,
    );
    const bagAmount = round2(
      quote.bagRequired
        ? bagPreTaxAfterDiscount * (1 + GST_RATES.bag / 100)
        : 0,
    );
    const bagDiscountAmount = round2(bagGrossAmount - bagAmount);

    // GST (reverse-calculated)
    const productGstAmount = round2(
      productAmount - (productAmount * 100) / (100 + productGstRate),
    );
    const engageGstAmount = round2(
      engageAmount - (engageAmount * 100) / (100 + GST_RATES.engage),
    );
    const bagGstAmount = quote.bagRequired
      ? round2(bagAmount - (bagAmount * 100) / (100 + GST_RATES.bag))
      : 0;
    const gstAmount = round2(
      productGstAmount + engageGstAmount + bagGstAmount + addOnsGstAmount,
    );

    const total = round2(productAmount + engageAmount + bagAmount + addOnsAmount);

    return {
      rate,
      units,
      grossAmount,
      discountAmount,
      productAmount,
      engageGrossAmount,
      engageDiscountAmount,
      engageAmount,
      engageAddOnAmounts,
      addOnsAmount,
      bagGrossAmount,
      bagDiscountAmount,
      bagAmount,
      gstAmount,
      total,
    };
  }, [quote, enginePlans]);

  const handleIdentityEditStart = () => {
    setIdentitySnapshot({
      doctorName,
      clinicName,
      clinicAddress,
      clinicCity,
      clinicState,
      clinicCountry,
      clinicPincode,
    });
    setIdentityEditable(true);
  };

  const handleIdentityCancel = () => {
    if (identitySnapshot) {
      setDoctorName(identitySnapshot.doctorName);
      setClinicName(identitySnapshot.clinicName);
      setClinicAddress(identitySnapshot.clinicAddress);
      setClinicCity(identitySnapshot.clinicCity);
      setClinicState(identitySnapshot.clinicState);
      setClinicCountry(identitySnapshot.clinicCountry);
      setClinicPincode(identitySnapshot.clinicPincode);
    }
    setIdentityEditable(false);
  };

  const handleIdentitySave = async () => {
    if (!doctorName.trim()) {
      toast.error("Doctor name is required");
      return;
    }
    if (!clinicName.trim()) {
      toast.error("Clinic name is required");
      return;
    }

    setSavingIdentity(true);
    try {
      const formData = new FormData();
      formData.append("doctor", JSON.stringify({ _id: doctorId, name: doctorName.trim() }));
      formData.append(
        "clinic",
        JSON.stringify({
          _id: clinicId,
          name: clinicName.trim(),
          address: clinicAddress.trim(),
          city: clinicCity.trim(),
          state: clinicState.trim(),
          country: clinicCountry.trim(),
          pincode: clinicPincode.trim(),
        }),
      );
      await apiService.updateDoctor(formData, token);
      toast.success("Doctor and clinic details updated");
      setIdentityEditable(false);
    } catch (error) {
      toast.error(
        error.response?.data?.message || error.message || "Failed to update doctor/clinic details",
      );
    } finally {
      setSavingIdentity(false);
    }
  };

  const handleSave = async () => {
    if (!quote.paymentTerm.trim()) {
      toast.error("Payment terms are required");
      return;
    }
    if (isInvoiceMode && !editReason.trim()) {
      toast.error("A reason for this correction is required");
      return;
    }
    if (!isInvoiceMode) {
      for (const [label, address] of [["Bill To", billTo], ["Ship To", shipTo]]) {
        if (address.sameAsClinic) continue;
        const missing = ADDRESS_REQUIRED.filter(([key]) => !String(address[key] || "").trim()).map(([, name]) => name);
        if (missing.length) {
          toast.error(`${label}: ${missing.join(", ")} required`);
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      const finalQuote = {
        product: quote.product,
        currency: quote.currency,
        units: Number(quote.units),
        rate: pricing.rate,
        grossAmount: pricing.grossAmount,
        discount: Number(quote.discount || 0),
        discountType: quote.discountType,
        discountAmount: pricing.discountAmount,
        productAmount: pricing.productAmount,
        engagePlan: quote.engagePlan,
        tenureMonths: Number(quote.tenureMonths),
        engagePrice: pricing.engageAmount,
        engageGrossAmount: pricing.engageGrossAmount,
        engageDiscount: Number(quote.engageDiscount || 0),
        engageDiscountType: quote.engageDiscountType,
        engageDiscountAmount: pricing.engageDiscountAmount,
        engageAddOns: (quote.engageAddOns || []).map((addOn, index) => ({
          plan: addOn.plan,
          tenureMonths: Number(addOn.tenureMonths),
          price: pricing.engageAddOnAmounts[index].amount,
          grossAmount: pricing.engageAddOnAmounts[index].grossAmount,
          discount: Number(addOn.discount || 0),
          discountType: addOn.discountType,
          discountAmount: pricing.engageAddOnAmounts[index].discountAmount,
        })),
        bagRequired: quote.bagRequired,
        bagPrice: pricing.bagAmount,
        bagGrossAmount: pricing.bagGrossAmount,
        bagDiscount: Number(quote.bagDiscount || 0),
        bagDiscountType: quote.bagDiscountType,
        bagDiscountAmount: pricing.bagDiscountAmount,
        gstAmount: pricing.gstAmount,
        paymentType: quote.paymentType,
        paymentTerm: quote.paymentTerm,
        expiryDate: quote.expiryDate || undefined,
        totalAmount: pricing.total,
      };

      const res = isInvoiceMode
        ? await apiService.updateInvoice(orderId, { ...finalQuote, reason: editReason }, token)
        : await apiService.updateQuote(orderId, finalQuote, token, { billTo, shipTo });
      toast.success(
        isInvoiceMode ? "Invoice revised in Zoho." : "Quotation revised. Review and send it again.",
      );
      onSaved(res.data);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          (isInvoiceMode ? "Failed to update invoice" : "Failed to update quotation"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: UI.surface,
          backgroundImage: "none",
          border: "1px solid",
          borderColor: UI.line,
          borderRadius: "10px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "10px",
          padding: "16px 18px",
          borderBottom: "1px solid",
          borderColor: UI.line,
        }}
      >
        <Box>
          <Box sx={{ fontSize: 16, fontWeight: 500, color: UI.ink }}>
            {isInvoiceMode ? "Edit invoice" : "Edit quotation"}
          </Box>
          {clinicName && (
            <Box sx={{ fontSize: 12, color: UI.ink3, marginTop: "2px" }}>
              {clinicName}
            </Box>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: "18px", bgcolor: UI.bg2 }}>
        {loading || !quote ? (
          <Box sx={{ py: 4 }}>
            <Loader />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                padding: "10px 13px",
                borderRadius: "4px",
                bgcolor: UI.amberBg,
                border: "1px solid",
                borderColor: UI.amberLine,
                color: UI.amberInk,
                fontSize: 12,
                marginBottom: "16px",
              }}
            >
              {isInvoiceMode
                ? "Editing revises the same invoice in Zoho. Only available before it's been emailed to the doctor."
                : "Editing revises the same quotation number in Zoho. It will need to be reviewed and sent again."}
            </Box>

            <SectionCard title="Clinic & doctor details">
              {isIdentityEditable ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label="Doctor name"
                    size="small"
                    fullWidth
                    value={doctorName}
                    onChange={(e) => setDoctorName(e.target.value)}
                  />
                  <TextField
                    label="Clinic name"
                    size="small"
                    fullWidth
                    value={clinicName}
                    onChange={(e) => setClinicName(e.target.value)}
                  />
                  <TextField
                    label="Address"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    value={clinicAddress}
                    onChange={(e) => setClinicAddress(e.target.value)}
                  />
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      label="City"
                      size="small"
                      fullWidth
                      value={clinicCity}
                      onChange={(e) => setClinicCity(e.target.value)}
                    />
                    <TextField
                      label="Pincode"
                      size="small"
                      fullWidth
                      value={clinicPincode}
                      onChange={(e) => setClinicPincode(e.target.value)}
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      select
                      label="Country"
                      size="small"
                      fullWidth
                      value={clinicCountry}
                      onChange={(e) => {
                        setClinicCountry(e.target.value);
                        setClinicState("");
                      }}
                    >
                      {identityCountries.map((country) => (
                        <MenuItem key={country.isoCode} value={country.isoCode}>
                          {country.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="State"
                      size="small"
                      fullWidth
                      value={clinicState}
                      disabled={!clinicCountry}
                      onChange={(e) => setClinicState(e.target.value)}
                    >
                      {identityStates.map((state) => (
                        <MenuItem key={state.isoCode} value={state.isoCode}>
                          {state.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box sx={{ fontSize: 11, color: UI.ink3 }}>
                    This updates the clinic/doctor record everywhere, not just this
                    quotation. Renaming the clinic also renames it on Zoho so past
                    quotes and invoices stay linked to the same billing contact.
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      onClick={handleIdentitySave}
                      disabled={savingIdentity}
                      sx={pillSx(true)}
                    >
                      {savingIdentity ? "Saving…" : "Save details"}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleIdentityCancel}
                      disabled={savingIdentity}
                      sx={pillSx(false)}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <Box sx={{ fontSize: 13, color: UI.ink }}>
                    <strong>Doctor:</strong> {doctorName || "—"}
                  </Box>
                  <Box sx={{ fontSize: 13, color: UI.ink }}>
                    <strong>Clinic:</strong> {clinicName || "—"}
                  </Box>
                  <Box sx={{ fontSize: 13, color: UI.ink }}>
                    <strong>Address:</strong>{" "}
                    {[
                      clinicAddress,
                      clinicCity,
                      identityStates.find((s) => s.isoCode === clinicState)?.name || clinicState,
                      clinicPincode,
                      identityCountries.find((c) => c.isoCode === clinicCountry)?.name ||
                        clinicCountry,
                    ]
                      .filter(Boolean)
                      .join(", ") || "—"}
                  </Box>
                  <Box sx={{ mt: "6px" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleIdentityEditStart}
                      sx={pillSx(false)}
                    >
                      Edit details
                    </Button>
                  </Box>
                </Box>
              )}
            </SectionCard>

            {!isInvoiceMode && (() => {
              const clinicAddressText = [
                clinicAddress,
                clinicCity,
                identityStates.find((s) => s.isoCode === clinicState)?.name || clinicState,
                clinicPincode,
                identityCountries.find((c) => c.isoCode === clinicCountry)?.name || clinicCountry,
              ]
                .filter(Boolean)
                .join(", ");
              const prefill = {
                addressLine1: clinicAddress,
                city: clinicCity,
                state: clinicState,
                country: clinicCountry,
                pincode: clinicPincode,
              };
              return (
                <SectionCard title="Bill to and ship to">
                  <AddressEditor
                    label="Bill To"
                    value={billTo}
                    onChange={setBillTo}
                    clinicSummary={[doctorName, clinicAddressText].filter(Boolean).join(", ")}
                    prefill={{ ...prefill, name: doctorName }}
                  />
                  <AddressEditor
                    label="Ship To"
                    value={shipTo}
                    onChange={setShipTo}
                    clinicSummary={[clinicName, clinicAddressText].filter(Boolean).join(", ")}
                    prefill={{ ...prefill, name: clinicName }}
                  />
                  <Box sx={{ fontSize: 11, color: UI.ink3 }}>
                    Saved with the quotation below, only on this order. A different Bill To state
                    changes the GST (place of supply).
                  </Box>
                </SectionCard>
              );
            })()}

            <SectionCard title="Product">
              <TextField
                select
                label="Product"
                size="small"
                fullWidth
                value={quote.product}
                onChange={(e) => setField("product", e.target.value)}
                sx={{ mb: 2 }}
              >
                {Object.keys(PRODUCTS).map((key) => (
                  <MenuItem key={key} value={key}>
                    {PRODUCTS[key].label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Currency"
                size="small"
                fullWidth
                value={quote.currency}
                onChange={(e) => setField("currency", e.target.value)}
                sx={{ mb: 2 }}
              >
                {currencies.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </TextField>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                }}
              >
                <TextField
                  label="Units"
                  type="number"
                  size="small"
                  value={quote.units}
                  onWheel={(e) => e.target.blur()}
                  onFocus={(e) => e.target.select()}
                  inputProps={{ min: 1 }}
                  onChange={(e) =>
                    setField("units", Math.max(1, Number(e.target.value) || 1))
                  }
                  sx={{ width: { xs: "100%", sm: 110 }, flexShrink: 0 }}
                />
                <DiscountFields
                  amountLabel="Product discount"
                  amount={quote.discount}
                  onAmountChange={(v) => setField("discount", v)}
                  type={quote.discountType}
                  onTypeChange={(v) => setField("discountType", v)}
                  targetGross={pricing?.grossAmount}
                  gstRate={GST_RATES[quote.product]}
                />
              </Box>
            </SectionCard>
<SectionCard title="Payment type">
  <ToggleRow
    options={[
      { label: "Advance payment", value: "advance" },
      { label: "EMI", value: "emi" },
    ]}
    value={quote.paymentType}
    onChange={(value) => setField("paymentType", value)}
  />
  <Box sx={{ fontSize: 11, color: UI.ink3, marginTop: "8px" }}>
    Switching to EMI starts that order on the standard not-yet-submitted EMI
    application flow. Switching away from EMI doesn't cancel an
    already-approved application — check the EMI status below first.
  </Box>
</SectionCard>
            <SectionCard title="Engage subscription">
              {Object.keys(enginePlans).length === 0 ? (
                <Box sx={{ fontSize: 12, color: UI.ink3 }}>
                  {enginePlansError
                    ? "Couldn't load plans from the plan model — refresh to retry."
                    : "Loading plans…"}
                </Box>
              ) : (
                <Box sx={{ mb: enginePlans[quote.engagePlan]?.yearly > 0 ? 2 : 0 }}>
                  <PillGroup
                    options={Object.entries(enginePlans).map(([key, plan]) => ({
                      value: key,
                      label: plan.label,
                    }))}
                    value={quote.engagePlan}
                    onChange={(v) => setField("engagePlan", v)}
                  />
                </Box>
              )}

              {enginePlans[quote.engagePlan]?.yearly > 0 && (
                <DiscountFields
                  amountLabel="Engage discount"
                  amount={quote.engageDiscount}
                  onAmountChange={(v) => setField("engageDiscount", v)}
                  type={quote.engageDiscountType}
                  onTypeChange={(v) => setField("engageDiscountType", v)}
                />
              )}

              <Box
                sx={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: UI.ink3,
                  marginTop: "18px",
                  marginBottom: "8px",
                }}
              >
                Tenure
              </Box>
              <PillGroup
                options={[
                  { value: 12, label: "1 YR" },
                  { value: 24, label: "2 YR" },
                  { value: 36, label: "3 YR" },
                ]}
                value={quote.customTenure ? null : quote.tenureMonths}
                onChange={(v) => {
                  setField("tenureMonths", v);
                  setField("customTenure", false);
                }}
              />
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant={quote.customTenure ? "contained" : "outlined"}
                  disableElevation
                  onClick={() => {
                    setField("customTenure", true);
                    setField("tenureMonths", "");
                  }}
                  sx={pillSx(Boolean(quote.customTenure))}
                >
                  Other
                </Button>
              </Box>
              {quote.customTenure && (
                <TextField
                  label="Custom tenure (months)"
                  type="number"
                  size="small"
                  fullWidth
                  value={quote.tenureMonths}
                  inputProps={{ min: 1 }}
                  onChange={(e) =>
                    setField(
                      "tenureMonths",
                      e.target.value === ""
                        ? ""
                        : Math.max(1, Number(e.target.value)),
                    )
                  }
                  sx={{ mt: 2 }}
                />
              )}
            </SectionCard>

            {(quote.engageAddOns || []).map((addOn, index) => (
              <SectionCard
                key={index}
                title={`Additional Engage plan ${index + 1}`}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <PillGroup
                    options={Object.entries(enginePlans)
                      .filter(([, plan]) => plan.yearly > 0)
                      .map(([key, plan]) => ({
                        value: key,
                        label: plan.label,
                      }))}
                    value={addOn.plan}
                    onChange={(v) => setEngageAddOnField(index, "plan", v)}
                  />
                  <Button
                    size="small"
                    color="error"
                    onClick={() => removeEngageAddOn(index)}
                  >
                    Remove
                  </Button>
                </Box>

                <DiscountFields
                  amountLabel="Engage discount"
                  amount={addOn.discount}
                  onAmountChange={(v) =>
                    setEngageAddOnField(index, "discount", v)
                  }
                  type={addOn.discountType}
                  onTypeChange={(v) =>
                    setEngageAddOnField(index, "discountType", v)
                  }
                />

                <Box
                  sx={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: UI.ink3,
                    marginTop: "18px",
                    marginBottom: "8px",
                  }}
                >
                  Tenure
                </Box>
                <PillGroup
                  options={[
                    { value: 12, label: "1 YR" },
                    { value: 24, label: "2 YR" },
                    { value: 36, label: "3 YR" },
                  ]}
                  value={addOn.customTenure ? null : addOn.tenureMonths}
                  onChange={(v) => {
                    setEngageAddOnField(index, "tenureMonths", v);
                    setEngageAddOnField(index, "customTenure", false);
                  }}
                />
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant={addOn.customTenure ? "contained" : "outlined"}
                    disableElevation
                    onClick={() => {
                      setEngageAddOnField(index, "customTenure", true);
                      setEngageAddOnField(index, "tenureMonths", "");
                    }}
                    sx={pillSx(Boolean(addOn.customTenure))}
                  >
                    Other
                  </Button>
                </Box>
                {addOn.customTenure && (
                  <TextField
                    label="Custom tenure (months)"
                    type="number"
                    size="small"
                    fullWidth
                    value={addOn.tenureMonths}
                    inputProps={{ min: 1 }}
                    onChange={(e) =>
                      setEngageAddOnField(
                        index,
                        "tenureMonths",
                        e.target.value === ""
                          ? ""
                          : Math.max(1, Number(e.target.value)),
                      )
                    }
                    sx={{ mt: 2 }}
                  />
                )}
              </SectionCard>
            ))}

            <Button
              size="small"
              variant="outlined"
              onClick={addEngageAddOn}
              sx={{ mb: 2 }}
            >
              + Add another Engage plan
            </Button>

            <SectionCard title="Carry bag">
              <Box sx={{ mb: quote.bagRequired ? 2 : 0 }}>
                <ToggleRow
                  options={[
                    {
                      label: `Yes, +${rupee(BAG_PRICE)} per unit`,
                      value: true,
                    },
                    { label: "No", value: false },
                  ]}
                  value={quote.bagRequired}
                  onChange={(value) => setField("bagRequired", value)}
                />
              </Box>
              {quote.bagRequired && (
                <DiscountFields
                  amountLabel="Bag discount"
                  amount={quote.bagDiscount}
                  onAmountChange={(v) => setField("bagDiscount", v)}
                  type={quote.bagDiscountType}
                  onTypeChange={(v) => setField("bagDiscountType", v)}
                />
              )}
            </SectionCard>

            {!isInvoiceMode && (
              <SectionCard title="Quotation validity">
                <TextField
                  type="date"
                  size="small"
                  label="Expiry date"
                  value={quote.expiryDate}
                  onChange={(e) => setField("expiryDate", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: { xs: "100%", sm: 200 } }}
                />
                <Box sx={{ fontSize: 11, color: UI.ink3, marginTop: "8px" }}>
                  Leave blank to keep the existing expiry.
                </Box>
              </SectionCard>
            )}

            <SectionCard title="Payment terms">
              <TextField
                required
                fullWidth
                multiline
                minRows={2}
                placeholder="Enter payment terms"
                value={quote.paymentTerm}
                onChange={(e) => setField("paymentTerm", e.target.value)}
              />
            </SectionCard>

            {isInvoiceMode && (
              <SectionCard title="Reason for this correction">
                <TextField
                  required
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder="Why is this invoice being corrected?"
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                />
                <Box sx={{ fontSize: 11, color: UI.ink3, marginTop: "8px" }}>
                  Zoho requires this to log the correction against the invoice.
                </Box>
              </SectionCard>
            )}

            {pricing && (
              <Box
                sx={{
                  padding: "13px 16px",
                  borderRadius: "8px",
                  marginBottom: "4px",
                  bgcolor: UI.p50,
                  border: "1px solid",
                  borderColor: UI.p100,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: "10px",
                  }}
                >
                  <Box sx={{ fontSize: 13, color: UI.ink3 }}>
                    New total (incl. GST)
                  </Box>
                  <Box
                    sx={{
                      fontSize: 18,
                      fontWeight: 500,
                      color: UI.p,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {rupee(pricing.total)}
                  </Box>
                </Box>
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column-reverse", sm: "row" },
                justifyContent: "flex-end",
                gap: "8px",
                marginTop: "22px",
                paddingTop: "16px",
                borderTop: "1px solid",
                borderTopColor: UI.line,
              }}
            >
              <Btn
                variant="ghost"
                text="Cancel"
                onClick={onClose}
                width="120px"
                sx={{ width: { xs: "100%", sm: "120px" } }}
              />
              <Btn
                text={isInvoiceMode ? "Save & update invoice" : "Save & revise quote"}
                onClick={handleSave}
                width="220px"
                sx={{ width: { xs: "100%", sm: "220px" } }}
                disabled={submitting}
              />
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EditQuoteModal;
